/**
 * Multiplayer Teen Patti Service
 *
 * Extends GenericMultiplayerService for online play.
 * Game type: 'teen-patti'
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  TeenPattiGameState,
  TeenPattiAction,
  Card,
  SideshowResult,
  ShowdownResult,
  TeenPattiActionRecord,
  RoundSummary,
} from '../types';

export class MultiplayerTeenPattiService extends GenericMultiplayerService<
  TeenPattiGameState,
  TeenPattiAction
> {
  private onCardsDealtCallback?: (cardCount: number) => void;
  private onCardsSeenCallback?: (cards: Card[]) => void;
  private onPlayerActionCallback?: (action: TeenPattiActionRecord) => void;
  private onPotUpdatedCallback?: (pot: number, currentStake: number) => void;
  private onSideshowRequestCallback?: (requesterId: string) => void;
  private onSideshowResultCallback?: (result: SideshowResult) => void;
  private onShowdownCallback?: (result: ShowdownResult) => void;
  private onRoundCompleteCallback?: (summary: RoundSummary) => void;
  private onPlayerEliminatedCallback?: (playerId: string) => void;
  private onNewRoundCallback?: (roundNumber: number) => void;
  private onTurnTimeoutCallback?: (playerId: string, countdown: number) => void;
  private onPlayerBlindSeenChangeCallback?: (playerId: string, isSeen: boolean) => void;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3;
  private playerSeat: number | null = null;

  constructor(wsService: WebSocketService) {
    super(wsService, 'teen-patti');
  }

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      const { action } = message;
      if (!action || !action.startsWith('teen-patti:')) return;
      const gameAction = action.replace('teen-patti:', '');

      switch (gameAction) {
        case 'roomCreated':
          this.roomCode = message.roomCode;
          this.playerRole = 'player1';
          this.playerSeat = message.seatIndex ?? 0;
          if (this.onGameStartedCallback) this.onGameStartedCallback();
          break;
        case 'roomJoined':
          this.roomCode = message.roomCode;
          this.playerRole = message.playerRole || 'player2';
          this.playerSeat = message.seatIndex ?? 1;
          if (this.onGameStartedCallback) this.onGameStartedCallback();
          break;
        case 'opponentJoined':
          if (this.onPlayerConnectedCallback) {
            this.onPlayerConnectedCallback(message.playerId || 'opponent');
          }
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;
        case 'gameStarted':
          if (message.gameState) {
            this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) this.onGameStateReceivedCallback(message.gameState);
          }
          if (this.onGameStartedCallback) this.onGameStartedCallback();
          break;
        case 'newRound':
          if (this.onNewRoundCallback) this.onNewRoundCallback(message.roundNumber);
          break;
        case 'cardsDealt':
          if (this.onCardsDealtCallback) this.onCardsDealtCallback(message.cardCount || 3);
          break;
        case 'cardsSeen':
          if (this.onCardsSeenCallback) this.onCardsSeenCallback(message.cards);
          break;
        case 'playerActed':
          if (this.onPlayerActionCallback) this.onPlayerActionCallback(message as TeenPattiActionRecord);
          break;
        case 'potUpdated':
          if (this.onPotUpdatedCallback) this.onPotUpdatedCallback(message.pot, message.currentStake);
          break;
        case 'sideshowRequest':
          if (this.onSideshowRequestCallback) this.onSideshowRequestCallback(message.requesterId);
          break;
        case 'sideshowResult':
          if (this.onSideshowResultCallback) this.onSideshowResultCallback(message as SideshowResult);
          break;
        case 'showdown':
          if (this.onShowdownCallback) this.onShowdownCallback(message.results);
          break;
        case 'roundComplete':
          if (this.onRoundCompleteCallback) this.onRoundCompleteCallback(message.summary);
          break;
        case 'playerEliminated':
          if (this.onPlayerEliminatedCallback) this.onPlayerEliminatedCallback(message.playerId);
          break;
        case 'playerBlindSeenChange':
          if (this.onPlayerBlindSeenChangeCallback) this.onPlayerBlindSeenChangeCallback(message.playerId, message.isSeen);
          break;
        case 'turnTimeout':
          if (this.onTurnTimeoutCallback) this.onTurnTimeoutCallback(message.playerId, message.secondsRemaining);
          break;
        case 'gameOver':
          if (this.onGameOverCallback) this.onGameOverCallback({ result: 'win', winner: message.winnerId, reason: 'game_complete' });
          break;
        case 'gameState':
          if (message.gameState) {
            this.gameState = message.gameState;
            this.reconnectAttempts = 0;
            if (this.onGameStateReceivedCallback) this.onGameStateReceivedCallback(message.gameState);
          }
          break;
        case 'opponentDisconnected':
          if (this.onPlayerDisconnectedCallback) this.onPlayerDisconnectedCallback(message.playerId);
          break;
        case 'opponentReconnected':
          if (this.onPlayerConnectedCallback) this.onPlayerConnectedCallback(message.playerId);
          break;
        case 'error':
          this.handleError(message.message || 'Unknown multiplayer error');
          break;
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({ action: 'teen-patti:createRoom', playerId: this.playerId, gameType: this.gameType });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({ action: 'teen-patti:joinRoom', roomCode, playerId: this.playerId, gameType: this.gameType });
  }

  protected sendLeaveRoomMessage(): void {
    this.wsService.send({ action: 'teen-patti:resign', roomCode: this.roomCode, playerId: this.playerId });
  }

  protected getMaxPlayers(): number { return 6; }

  async sendMove(move: TeenPattiAction): Promise<boolean> {
    try {
      this.wsService.send({ action: 'teen-patti:action', roomCode: this.roomCode, playerId: this.playerId, playerAction: move });
      return true;
    } catch (error) {
      this.handleError('Failed to send action', error);
      return false;
    }
  }

  sendStartGame(): Promise<boolean> {
    try {
      this.wsService.send({ action: 'teen-patti:startGame', roomCode: this.roomCode, playerId: this.playerId });
      return Promise.resolve(true);
    } catch { return Promise.resolve(false); }
  }

  isMyTurn(currentTurn: string): boolean {
    return this.playerId === currentTurn || this.playerRole === currentTurn;
  }

  async resign(): Promise<void> {
    this.wsService.send({ action: 'teen-patti:resign', roomCode: this.roomCode, playerId: this.playerId });
  }

  getPlayerSeat(): number | null { return this.playerSeat; }

  // Event callbacks
  onCardsDealt(cb: (count: number) => void): void { this.onCardsDealtCallback = cb; }
  onCardsSeen(cb: (cards: Card[]) => void): void { this.onCardsSeenCallback = cb; }
  onPlayerAction(cb: (action: TeenPattiActionRecord) => void): void { this.onPlayerActionCallback = cb; }
  onPotUpdated(cb: (pot: number, stake: number) => void): void { this.onPotUpdatedCallback = cb; }
  onSideshowRequest(cb: (requesterId: string) => void): void { this.onSideshowRequestCallback = cb; }
  onSideshowResult(cb: (result: SideshowResult) => void): void { this.onSideshowResultCallback = cb; }
  onShowdown(cb: (result: ShowdownResult) => void): void { this.onShowdownCallback = cb; }
  onRoundComplete(cb: (summary: RoundSummary) => void): void { this.onRoundCompleteCallback = cb; }
  onPlayerEliminated(cb: (playerId: string) => void): void { this.onPlayerEliminatedCallback = cb; }
  onNewRound(cb: (roundNumber: number) => void): void { this.onNewRoundCallback = cb; }
  onTurnTimeout(cb: (playerId: string, countdown: number) => void): void { this.onTurnTimeoutCallback = cb; }
  onPlayerBlindSeenChange(cb: (playerId: string, isSeen: boolean) => void): void { this.onPlayerBlindSeenChangeCallback = cb; }

  // Connection management
  handleDisconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const d = Math.pow(2, this.reconnectAttempts - 1) * 1000;
      setTimeout(() => this.reconnect(), d);
    }
  }

  reconnect(): void {
    if (this.roomCode) {
      this.wsService.send({ action: 'teen-patti:reconnect', roomCode: this.roomCode, playerId: this.playerId });
    }
  }

  getReconnectAttempts(): number { return this.reconnectAttempts; }
}
